import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Search, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import BackToDemoButton from "../components/BackToDemoButton";

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAssessmentResult, setIsAssessmentResult] = useState(false);

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${BACKEND_URL}/api`;
      
      try {
        // Check if this is from an assessment
        const fromAssessment = searchParams.get('assessment') === 'true';
        setIsAssessmentResult(fromAssessment);
        
        if (fromAssessment) {
          // Use assessment matching endpoint
          const assessmentData = {
            industry: searchParams.get('industry') || 'All Industries',
            company_size: searchParams.get('company_size') || '',
            challenge: searchParams.get('category') || ''
          };
          
          console.log('Assessment matching with:', assessmentData);
          
          const response = await axios.post(`${API}/ai-tools/match-assessment`, assessmentData);
          setResults(response.data);
        } else if (query) {
          // Use regular search endpoint
          const response = await axios.post(`${API}/ai-tools/search`, {
            query: query,
            industry: searchParams.get('industry') || null,
            category: searchParams.get('category') || null,
            company_size: searchParams.get('company_size') || null
          });
          setResults(response.data);
        }
      } catch (error) {
        console.error("Error fetching results:", error);
        // Fallback to mock data if API fails
        setResults([
          {
            id: 1,
            name: "CustomerBot AI",
            description: "AI-powered customer support automation for e-commerce",
            category: "Customer Support & Service",
            rating: 4.8,
            pricing: "From $99/mo",
            website: "https://example.com"
          },
          {
            id: 2,
            name: "SupportGenius",
            description: "Intelligent chatbot with natural language understanding",
            category: "Customer Support & Service",
            rating: 4.6,
            pricing: "From $149/mo",
            website: "https://example.com"
          },
          {
            id: 3,
            name: "HelpDesk Pro AI",
            description: "Complete customer service automation platform",
            category: "Customer Support & Service",
            rating: 4.7,
            pricing: "From $199/mo",
            website: "https://example.com"
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [query, searchParams]);

  return (
    <div className="min-h-screen bg-white dark:bg-[#121212]">
      <BackToDemoButton />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#121212]/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex justify-between items-center h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft size={20} />
            <img src="/logo.svg" alt="AI Fit Engine Logo" className="w-8 h-8" />
            <span className="text-xl sm:text-2xl font-bold">AI Fit Engine</span>
          </button>
          <div className="flex items-center gap-3 sm:gap-6">
            <button
              onClick={() => navigate('/about')}
              className="px-3 sm:px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-xs sm:text-sm font-medium transition-colors"
            >
              About
            </button>
            <button
              onClick={() => navigate('/startup/login')}
              className="px-3 sm:px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-xs sm:text-sm font-medium transition-colors"
            >
              For Startups
            </button>
          </div>
        </div>
      </header>

      {/* Search Results */}
      <div className="pt-24 pb-16 max-w-6xl mx-auto px-4 sm:px-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {isAssessmentResult ? "Your Personalized Recommendations" : "Search Results"}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {isAssessmentResult ? (
              <>
                Based on your assessment, we found <span className="font-semibold text-orange-600">{results.length} AI tools</span> perfectly matched to your needs
                {searchParams.get('industry') && (
                  <span> for <span className="font-semibold">{searchParams.get('industry')}</span></span>
                )}
              </>
            ) : (
              <>
                Found {results.length} AI tools {query && <>matching: <span className="font-semibold text-orange-600">"{query}"</span></>}
              </>
            )}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-600 dark:text-gray-400 text-lg mb-4">
              No AI tools found matching your criteria.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              Try a Different Search
            </Button>
          </div>
        ) : (
          <div className="grid gap-6">
            {results.map((result) => (
              <div
                key={result.id}
                className="bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                        {result.name}
                      </h3>
                      <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-xs rounded-full">
                        {result.category}
                      </span>
                      <span className="text-sm text-gray-900 dark:text-gray-100 font-semibold">
                        ⭐ {result.rating}
                      </span>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {result.description}
                    </p>
                    {result.features && result.features.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {result.features.slice(0, 4).map((feature, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-sm flex-wrap">
                      <span className="text-gray-600 dark:text-gray-400">
                        <strong>Pricing:</strong> {result.pricing}
                      </span>
                      {result.implementation_time && (
                        <>
                          <span className="text-gray-500">•</span>
                          <span className="text-gray-600 dark:text-gray-400">
                            <strong>Setup:</strong> {result.implementation_time}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => window.open(result.website, '_blank')}
                      className="bg-orange-500 hover:bg-orange-600 text-white whitespace-nowrap"
                    >
                      Visit Website
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPage;